/**
 * DatePicker.tsx — a themed, accessible UK date field + calendar popover.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: kit.tsx's original design note ("native inputs only … no date/select
 * libraries") held while the only date field was a plain browser
 * `<input type="date">` — but that renders the OS's own date UI, which for a
 * date-of-birth question (typically 20–70 years back) forces dozens of
 * prev/next-month taps. This component keeps the "no external library" rule
 * (it is hand-built from native `<input>`/`<select>` elements, zero added
 * dependencies) while fixing the UX: a text field the patient can type into
 * directly, PLUS a calendar button that opens a popover with fast month/year
 * `<select>` jumps (year spans ~1920 → current year) and a keyboard-operable
 * day grid.
 *
 * THE FIELD READS AND WRITES BRITISH `dd/mm/yyyy`; THE VALUE CONTRACT IS ISO
 * `yyyy-mm-dd` (2026-08-19, CTO). A UK patient asked for their date of birth
 * writes 27/10/1996 — showing them 1996-10-27 and demanding they type it
 * year-first is a foreign format on a form that is already the highest-drop-off
 * question in the journey, and "03/04/2026" typed into an ISO field silently
 * became 3 March instead of 3 April. So the DISPLAY and the TYPING are UK, and
 * ONLY those: the moment eight digits name a real calendar day the value handed
 * to the caller flips back to ISO `yyyy-mm-dd` ({@link isoFromTyped}), because
 * every caller (QuestionCard's date question, the registration DOB field, the
 * staff single-page form, the admin audit filter bar) hands ISO in, stores ISO,
 * and posts to servers that parse ISO. A caller cannot tell this component
 * changed except that the patient now sees their own date format.
 *
 * TYPING IS MASKED into the UK skeleton as the patient goes (see
 * {@link maskUkDate}): the field used to pass the raw keystrokes through, so
 * "27101996" — the obvious way to type a date of birth on a numeric keypad —
 * stayed "27101996", parsed as nothing, and left the patient stuck with no
 * explanation. A half-typed value ("27/10") still travels up exactly as the
 * patient left it; the caller, not this component, decides when a partial date
 * is acceptable, and every caller already rejects anything that is not a whole
 * ISO date. Only FORMAT validity is enforced here (31/02 is not a day, so it is
 * never dressed up as an ISO date) — POLICY (in range, over 18) stays with the
 * caller and the server, exactly as before.
 *
 * THE WHOLE FIELD OPENS THE CALENDAR (2026-08-19, CTO) — see
 * {@link DatePicker}'s `handleInputMouseDown` for how that co-exists with
 * typing, which is the faster path and the reason this component exists.
 *
 * AN EMPTY DOB CALENDAR OPENS EIGHTEEN YEARS BACK (2026-09-04, CTO: "mine
 * loads 2026, but patients should be 18 and above") — opt in per caller with
 * `openView="dob"`, because the audit filter bar wants today. ONLY THE BROWSED
 * MONTH MOVES: the field stays empty (a date of birth the form pre-filled is
 * one the patient never entered, and the R2 age gate would be judging it), and
 * `max` stays whatever the caller passed — today on every DOB field — so an
 * under-18 can still enter their real date, reach the server and be
 * signposted. See {@link browsedMonthFor}.
 *
 * A11y: follows the WAI-ARIA "Date Picker Dialog" pattern — the popover is a
 * labelled `role="dialog"` containing a `role="grid"` of days with a roving
 * tabindex (arrow keys move the day-in-focus, Enter/Space selects, Escape
 * closes and returns focus to whichever control opened it). The popover closes
 * on an outside click or when focus leaves the widget entirely (blur).
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

/** See {@link DatePickerProps.openView}. */
export type DatePickerOpenView = 'today' | 'dob';

export interface DatePickerProps {
  /** id placed on the visible text input — bind a <label htmlFor> to this. */
  id: string;
  /**
   * Current value as an ISO `yyyy-mm-dd` string, or '' when empty. It is
   * RENDERED to the patient in UK `dd/mm/yyyy` — callers keep speaking ISO.
   */
  value: string;
  /**
   * Fired with the ISO `yyyy-mm-dd` date once eight typed digits name a real
   * calendar day (or a day is picked from the calendar). Mid-type it is the
   * partial UK string the patient can see in the field ('27/10'), which is why
   * this stays a plain string rather than a validated date — the caller owns
   * "is this date acceptable yet", exactly as it did before.
   */
  onChange: (value: string) => void;
  /** Latest selectable date (ISO `yyyy-mm-dd`) — e.g. today for a DOB field. */
  max?: string;
  /** Earliest selectable date (ISO `yyyy-mm-dd`) — mirrors `max`. */
  min?: string;
  disabled?: boolean;
  placeholder?: string;
  /** Analytics-density field chrome (UX Wave 7B §7 — the audit filter bar):
   * a shorter input + smaller type. The popover calendar is unchanged. */
  compact?: boolean;
  /**
   * Which month an EMPTY calendar OPENS on. A picker that already has a value
   * always opens on that value, so this can never move a patient off their own
   * answer, and it never writes anything into the field.
   *
   * 'today' (default) is right for a near-date field — the admin audit filter
   * is looking for something that happened this week, not in 2008. 'dob' opens
   * {@link ADULT_AGE_YEARS} years back, the youngest date of birth the age gate
   * accepts (see {@link browsedMonthFor}).
   *
   * Defaulting rather than switching globally is deliberate: "18 years ago" is
   * nonsense on a filter, so the callers that mean a date of birth say so, and
   * every other date field keeps the behaviour it was reviewed with.
   */
  openView?: DatePickerOpenView;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const WEEKDAYS = [
  { abbr: 'Mon', full: 'Monday' },
  { abbr: 'Tue', full: 'Tuesday' },
  { abbr: 'Wed', full: 'Wednesday' },
  { abbr: 'Thu', full: 'Thursday' },
  { abbr: 'Fri', full: 'Friday' },
  { abbr: 'Sat', full: 'Saturday' },
  { abbr: 'Sun', full: 'Sunday' },
];

const EARLIEST_YEAR = 1920;

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function toIso(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Parse a `yyyy-mm-dd` string as a LOCAL date (never UTC-shifted). */
function parseIso(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const [, y, m, d] = match;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

/**
 * Do these three numbers name a real calendar day? (`month` is 1-based here —
 * this reads a patient's typing, not a Date's internals.)
 *
 * WHY THE FOUR-DIGIT YEAR FLOOR: `new Date(96, 9, 27)` is 1996, not the year 96
 * — the Date constructor quietly maps 0–99 into the 1900s. Eight typed digits
 * always give a four-digit year, so anything under 1000 is a typo, and letting
 * it through would hand the caller an ISO date the patient never entered.
 *
 * WHY THIS LIVES HERE AT ALL: this component now BUILDS the ISO string rather
 * than passing one through, so it must not manufacture "1996-02-31". A value
 * shaped like an ISO date is treated by every caller as a real one — that is
 * precisely the string that reaches an ISO-parsing server and rolls over into
 * 2 March without anybody noticing. Impossible days stay visible, half-typed
 * text for the caller to reject; they are never promoted to ISO. Note this is
 * FORMAT validity only — whether the date is in range, or old enough, remains
 * the caller's and the server's call.
 */
function isRealDate(day: number, month: number, year: number): boolean {
  if (year < 1000 || month < 1 || month > 12 || day < 1) return false;
  return day <= daysInMonth(year, month - 1);
}

/**
 * Reshape whatever is in the text field into the UK `dd/mm/yyyy` skeleton.
 *
 * WHY: this field used to hand `e.target.value` straight back to the caller, so
 * a patient typing "27101996" — the single most natural way to type a date of
 * birth on a numeric keypad — kept "27101996" forever. It never becomes a date,
 * so the calendar could not follow it, the caller stored a string no server
 * would accept, and NOTHING on screen explained why: the stakeholder walk
 * -through stalled on exactly this. Inserting the slashes as they type removes
 * the guess entirely.
 *
 * THE SEPARATOR IS NEVER APPENDED, only ever placed BEFORE a digit that has
 * arrived. That single rule is what stops the mask fighting a deletion: if "27"
 * auto-grew to "27/", backspace would delete the slash, the mask would put it
 * straight back, and the field would be frozen two characters from the end —
 * the classic date-mask trap. Because a group's separator only exists while
 * that group has a digit in it, backspacing simply walks back out of the value,
 * and a half-typed "27/1" survives the round trip untouched.
 *
 * Non-digits are dropped, which is what makes a paste of "27-10-1996" (or
 * "27101996") land correctly rather than being re-punctuated into nonsense.
 * Anything past 8 digits is discarded — a date of birth has no more.
 */
export function maskUkDate(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * UK text → the ISO `yyyy-mm-dd` value the caller gets, or '' while it is not
 * yet a whole, real date.
 *
 * DAY FIRST, ALWAYS. "03/04/2026" is 3 April — reading it American-style as
 * 4 March is the failure this whole change exists to prevent, and on a date of
 * birth it is a silent one: both orderings parse, so nothing errors, the wrong
 * date is simply stored against a patient's record.
 */
export function isoFromUk(text: string): string {
  const digits = text.replace(/\D/g, '');
  if (digits.length !== 8) return '';
  const day = Number(digits.slice(0, 2));
  const month = Number(digits.slice(2, 4));
  const year = Number(digits.slice(4));
  if (!isRealDate(day, month, year)) return '';
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/**
 * ISO `yyyy-mm-dd` → the UK `dd/mm/yyyy` the patient reads, or '' when the
 * string is not a whole, real ISO date (a half-typed value has nothing to
 * convert and must be shown back exactly as the patient left it).
 */
export function ukFromIso(iso: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return '';
  const [, y, m, d] = match;
  if (!isRealDate(Number(d), Number(m), Number(y))) return '';
  return `${d}/${m}/${y}`;
}

/** What the field SHOWS for a given caller value: UK for a whole ISO date,
 * otherwise the in-progress text unchanged. */
export function displayDate(value: string): string {
  return ukFromIso(value) || value;
}

/**
 * The ISO value a typed or pasted string settles to, or '' while it is not yet
 * a whole date.
 *
 * THE ISO ESCAPE HATCH is deliberate: a whole `yyyy-mm-dd` string is the one
 * shape a UK typist can never produce here (typing only ever yields digits and
 * the slashes this component inserts, and a day is never four digits), so it
 * can only have been pasted — from an email, a spreadsheet cell, a copied
 * record. Reading it as ISO rather than shredding it into "19/90/0105" costs
 * nothing and loses no ambiguity, because the day-first rule only ever applies
 * to the slashed form.
 *
 * IT READS THE MASKED TEXT, NOT THE RAW: the mask is what discards a ninth
 * digit, so reading the raw string here would leave the field SHOWING a whole
 * date while the caller was told there wasn't one — the field and the value
 * disagreeing is the one failure that has no symptom until an order is placed
 * with no date of birth on it.
 */
export function isoFromTyped(raw: string): string {
  const trimmed = raw.trim();
  if (ukFromIso(trimmed)) return trimmed;
  return isoFromUk(maskUkDate(trimmed));
}

/**
 * Where the caret belongs in the MASKED value, given where it sat in the raw
 * one. Counts digits rather than characters: the mask adds and removes slashes
 * around the caret, so a raw character offset would drift by one every time a
 * separator appeared. Editing the month of an already-complete date is the case
 * that matters — without this, React re-renders the reshaped value and the
 * browser dumps the caret at the end, so the next keystroke lands in the year.
 */
export function caretForDigits(masked: string, digitsBefore: number): number {
  if (digitsBefore <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < masked.length; i += 1) {
    if (/\d/.test(masked[i])) {
      seen += 1;
      if (seen === digitsBefore) return i + 1;
    }
  }
  return masked.length;
}

/** Build a date, clamping the day into the target month (no rollover). */
function safeDate(year: number, month: number, day: number): Date {
  return new Date(year, month, Math.min(day, daysInMonth(year, month)));
}

function addDays(date: Date, delta: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + delta);
  return next;
}

function todayIso(): string {
  return toIso(new Date());
}

/**
 * The age gate every patient-facing date of birth is judged against (R2:
 * under 18 is a hard stop WITH signposting, under 13 is never persisted).
 *
 * IT LIVES HERE, ONCE, AND ONLY MOVES THE CAMERA. Each DOB call site working
 * out "eighteen years ago" for itself would put the number in three files and
 * let them drift; and nothing in this component may ENFORCE it — `min`/`max`
 * stay exactly what the caller passed, so a 16-year-old can still pick a
 * recent date, reach the server and get the stop screen. A picker that refused
 * recent dates would leave them unable to submit at all, deleting the
 * safeguarding path in the name of a tidier calendar.
 */
const ADULT_AGE_YEARS = 18;

/**
 * WHICH MONTH THE CALENDAR BROWSES for a given value. An existing value ALWAYS
 * wins — the calendar opens where the patient's own answer is, and the opening
 * view below is only ever the fallback for an empty (or still half-typed)
 * field.
 *
 * WHY THE EMPTY FALLBACK MOVED: it was today for every field, so a patient
 * hunting for 1994 opened the calendar in 2026 and had to page back 32 years,
 * on the highest-drop-off question in the journey. Eighteen years back is the
 * nearest edge of the range every real date of birth sits in, and the year
 * `<select>` covers the rest in one jump. It is a VIEW, not a value: nothing is
 * written to the field, and the caller's `min`/`max` are untouched.
 *
 * ONE FUNCTION because the answer is needed twice — as the initial browsed
 * month, and again on every open, which must pick up a value that changed in
 * between (a caller reset, a date typed and then cleared). Two copies of this
 * rule would drift, and the drift is invisible: the field would simply open on
 * the wrong month for whoever hit the path nobody re-read.
 */
function browsedMonthFor(value: string, openView: DatePickerOpenView): Date {
  const existing = parseIso(value);
  if (existing) return existing;
  const now = new Date();
  if (openView === 'today') return now;
  // safeDate, not a bare `new Date(y - 18, m, d)`: asked on 29 February the
  // shifted year has no 29th, and the Date constructor would roll over to
  // 1 March — browsing the wrong month on the one day a year it happens.
  return safeDate(now.getFullYear() - ADULT_AGE_YEARS, now.getMonth(), now.getDate());
}

interface DayCell {
  date: Date;
  iso: string;
  inMonth: boolean;
  disabled: boolean;
}

function buildWeeks(year: number, month: number, min: string | undefined, max: string | undefined): DayCell[][] {
  const first = new Date(year, month, 1);
  const mondayIndex = (first.getDay() + 6) % 7; // 0 = Monday
  const start = addDays(first, -mondayIndex);
  const weeks: DayCell[][] = [];
  let cursor = start;
  for (let w = 0; w < 6; w += 1) {
    const week: DayCell[] = [];
    for (let d = 0; d < 7; d += 1) {
      const iso = toIso(cursor);
      week.push({
        date: cursor,
        iso,
        inMonth: cursor.getMonth() === month,
        disabled: Boolean(max && iso > max) || Boolean(min && iso < min),
      });
      cursor = addDays(cursor, 1);
    }
    weeks.push(week);
  }
  return weeks;
}

const fieldWrap: React.CSSProperties = { position: 'relative' };

const inputRowStyle: React.CSSProperties = { position: 'relative' };

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1.5px solid var(--gps-border-strong)',
  borderRadius: 'var(--gps-r-md)',
  padding: '13px 48px 13px 14px',
  font: 'inherit',
  fontSize: '16px',
  minHeight: '48px',
  background: 'var(--gps-surface)',
  color: 'var(--gps-text)',
  // The whole row opens the calendar, so the row must read as clickable —
  // but it is a text field first, so it keeps the text caret, not a pointer.
  cursor: 'text',
};

/** Analytics-density overrides for the audit filter bar (UX Wave 7B §7). */
const compactInputStyle: React.CSSProperties = {
  padding: '6px 38px 6px 10px',
  fontSize: '13px',
  minHeight: '34px',
  borderRadius: 'var(--gps-r-sm)',
};

const compactIconButtonStyle: React.CSSProperties = {
  width: '30px',
  height: '30px',
};

const iconButtonStyle: React.CSSProperties = {
  position: 'absolute',
  right: '2px',
  top: '2px',
  width: '44px',
  height: '44px',
  display: 'grid',
  placeItems: 'center',
  border: 0,
  background: 'transparent',
  borderRadius: 'var(--gps-r-md)',
  color: 'var(--gps-green-text)',
  cursor: 'pointer',
};

const popoverStyle: React.CSSProperties = {
  position: 'absolute',
  zIndex: 20,
  top: 'calc(100% + 6px)',
  left: 0,
  width: 'min(320px, 92vw)',
  background: 'var(--gps-surface)',
  border: '1px solid var(--gps-border-strong)',
  borderRadius: 'var(--gps-r-lg)',
  boxShadow: 'var(--gps-shadow)',
  padding: 'var(--gps-s-3)',
};

const selectStyle: React.CSSProperties = {
  flex: 1,
  boxSizing: 'border-box',
  border: '1.5px solid var(--gps-border-strong)',
  borderRadius: 'var(--gps-r-md)',
  padding: '9px 8px',
  font: 'inherit',
  fontSize: '14px',
  minHeight: '40px',
  background: 'var(--gps-surface)',
  color: 'var(--gps-text)',
};

/** Off-screen but announced — the format instruction below is for assistive
 * tech, where a placeholder is not a reliable substitute (WCAG 3.3.2: it is
 * gone the moment the patient types the first digit). */
const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** Calendar-glyph icon (drawn inline — no icon library). */
function CalendarGlyph() {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none">
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M3.5 9.5H20.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M8 3V6.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M16 3V6.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

/**
 * A themed UK date field: a typeable text input (`dd/mm/yyyy`) plus a calendar
 * button that opens a popover with month/year jump `<select>`s and a
 * keyboard-operable day grid. The value in and out is ISO `yyyy-mm-dd`.
 */
export function DatePicker({ id, value, onChange, max, min, disabled, placeholder = 'DD/MM/YYYY', compact = false, openView = 'today' }: DatePickerProps) {
  const [open, setOpen] = useState(false);
  /**
   * Does the day grid currently own keyboard focus?
   *
   * WHY THIS IS SEPARATE FROM `open`: the popover can now be opened two ways
   * with two different focus contracts. Opened from the calendar button (or
   * ArrowDown) it is a dialog the user asked to work in, so focus moves into
   * the grid per the WAI-ARIA pattern. Opened by clicking the FIELD it is an
   * offer, not a takeover — the caret must stay under the patient's fingers or
   * the "just type it" path this component was built for is dead on arrival.
   */
  const [gridFocus, setGridFocus] = useState(false);
  const [focusDate, setFocusDate] = useState<Date>(() => browsedMonthFor(value, openView));
  const wrapperRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Which control opened the popover — Escape and a day-pick return focus
   * there, so a patient who opened it by clicking the field lands back on the
   * caret rather than being dumped on the icon button. */
  const openerRef = useRef<'field' | 'button'>('button');
  /** Caret offset to reapply after the masked value re-renders (null = leave it). */
  const pendingCaret = useRef<number | null>(null);
  const dayRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const popoverId = `${id}-calendar`;
  const formatHintId = `${id}-format`;
  const shown = displayDate(value);

  const effectiveMax = max ?? undefined;
  const effectiveMin = min ?? undefined;
  const maxYear = effectiveMax ? (parseIso(effectiveMax)?.getFullYear() ?? new Date().getFullYear()) : new Date().getFullYear();
  const minYear = effectiveMin ? (parseIso(effectiveMin)?.getFullYear() ?? EARLIEST_YEAR) : EARLIEST_YEAR;
  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = minYear; y <= maxYear; y += 1) out.push(y);
    return out;
  }, [minYear, maxYear]);

  const year = focusDate.getFullYear();
  const month = focusDate.getMonth();
  const weeks = useMemo(() => buildWeeks(year, month, effectiveMin, effectiveMax), [year, month, effectiveMin, effectiveMax]);
  const focusIso = toIso(focusDate);
  const today = todayIso();

  /** Clamp a navigation target into the [min, max] range (mirrored bounds). */
  function clampToRange(date: Date): Date {
    if (effectiveMax && toIso(date) > effectiveMax) return parseIso(effectiveMax) ?? date;
    if (effectiveMin && toIso(date) < effectiveMin) return parseIso(effectiveMin) ?? date;
    return date;
  }

  function openPopover(opener: 'field' | 'button', focusGrid: boolean) {
    if (disabled) return;
    openerRef.current = opener;
    // Re-anchored on EVERY open, not just the first: the value may have changed
    // since (see {@link browsedMonthFor}).
    setFocusDate(clampToRange(browsedMonthFor(value, openView)));
    setOpen(true);
    setGridFocus(focusGrid);
  }

  function closePopover(returnFocus: boolean) {
    setOpen(false);
    setGridFocus(false);
    if (returnFocus) (openerRef.current === 'field' ? inputRef.current : iconRef.current)?.focus();
  }

  /**
   * Typing: reshape into the UK skeleton, hand ISO up the moment it is a whole
   * real date, and get the calendar out of the way.
   *
   * WHY TYPING CLOSES THE POPOVER: a patient who clicked the field to open the
   * calendar and then started typing has chosen the faster path, and the
   * calendar has nothing left to offer — it would sit below the field showing
   * a month nobody is looking at, squeezed against the on-screen keyboard on a
   * phone, re-laying-out under the typist's thumbs as the month select follows
   * each keystroke. Closing it also removes any chance of the grid's roving
   * tabindex stealing the caret mid-date.
   *
   * The value stays CONTROLLED by the caller, so the reshaped string only
   * reaches the input after the caller echoes it back. That round trip is what
   * costs the caret its place, so the digit offset is measured here (against the
   * raw string the browser just produced) and reapplied below once the masked
   * value has actually painted.
   */
  function handleTextChange(event: React.ChangeEvent<HTMLInputElement>) {
    const raw = event.target.value;
    const caret = event.target.selectionStart ?? raw.length;
    const digitsBefore = raw.slice(0, caret).replace(/\D/g, '').length;
    const iso = isoFromTyped(raw);
    const next = iso || maskUkDate(raw);
    // Measured against what the field will SHOW, not what travels up: those are
    // different strings the moment the value settles into ISO.
    pendingCaret.current = caretForDigits(displayDate(next), digitsBefore);
    if (open) closePopover(false);
    onChange(next);
  }

  // Reapply the caret in the SAME frame the masked value paints (layout, not
  // passive): a passive effect lets the browser show the caret at the end of the
  // field for a frame first, which reads as the field jumping under the typist.
  // No dependency list — this also reconciles the DOM below, which has to happen
  // on the renders where React itself decides nothing changed.
  useLayoutEffect(() => {
    const el = inputRef.current;
    const caret = pendingCaret.current;
    pendingCaret.current = null;
    if (!el) return;
    // A rejected character (a letter, a ninth digit) leaves the mask output
    // IDENTICAL to the current value, so React re-renders nothing and the
    // browser is left showing the character we refused. Write it back out.
    if (el.value !== shown) el.value = shown;
    if (caret === null) return;
    // Only ever touch a field the patient is actually typing in — a programmatic
    // value change (calendar pick, caller reset) must not steal focus or move a
    // caret that belongs to some other control.
    if (document.activeElement !== el) return;
    el.setSelectionRange(caret, caret);
  });

  /**
   * THE WHOLE FIELD OPENS THE CALENDAR — but only the click that ENTERS it.
   *
   * The CTO's ask was that the calendar not hide behind a 44px icon: "it should
   * work for the entire row, the entire field". The constraint pulling the
   * other way is that this component exists BECAUSE typing a date 30 years back
   * beats tapping through 360 months, so the field must stay a text field.
   *
   * The rule that satisfies both: a click on a field that does NOT yet hold the
   * caret opens the calendar (and leaves the caret in the field, so the patient
   * can ignore the calendar and type). Clicks inside a field that is already
   * focused do nothing — because those clicks are the patient positioning their
   * caret to fix a digit, and three bad things follow from treating them as a
   * trigger: the popover toggles under someone mid-correction; a calendar just
   * dismissed with Escape springs straight back on the next click, which is the
   * one thing a dismissal must never do; and re-opening would re-anchor the
   * browsed month, throwing away wherever they had navigated to.
   *
   * mousedown, not click, because it is the last moment at which "was this
   * field already focused?" can still be asked — by click time the browser has
   * already moved focus here. The icon button (and ArrowDown) remain the
   * deliberate re-open, so nothing is unreachable from the keyboard.
   */
  function handleInputMouseDown() {
    if (disabled || open) return;
    if (document.activeElement === inputRef.current) return;
    openPopover('field', false);
  }

  function handleInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') {
      if (!open) return;
      event.preventDefault();
      // Opened from the field, so focus returns to the field: the caret is where
      // the patient was working, and Escape must not relocate them.
      closePopover(true);
      return;
    }
    if (event.key === 'ArrowDown') {
      // The keyboard route from the field into the grid — a keyboard user should
      // not have to Tab past the field to reach the calendar they can already
      // see. Escape still hands the caret back here.
      event.preventDefault();
      if (open) setGridFocus(true);
      else openPopover('field', true);
    }
  }

  function selectDay(cell: DayCell) {
    if (cell.disabled) return;
    onChange(cell.iso);
    closePopover(true);
  }

  // Keep the focused grid cell in sync with keyboard/select navigation — but
  // ONLY once the grid actually owns focus, or opening the popover by clicking
  // the field would rip the caret out of the field on the same tick.
  useEffect(() => {
    if (!open || !gridFocus) return;
    dayRefs.current[focusIso]?.focus();
  }, [open, gridFocus, focusIso]);

  // Close on outside click (belt-and-braces alongside the blur handler below,
  // which covers focus moving away but not every mouse interaction).
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(event: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpen(false);
        setGridFocus(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  function handleWrapperBlur(event: React.FocusEvent<HTMLDivElement>) {
    const next = event.relatedTarget as Node | null;
    if (wrapperRef.current && next && wrapperRef.current.contains(next)) return;
    setOpen(false);
    setGridFocus(false);
  }

  function handleMonthSelect(event: React.ChangeEvent<HTMLSelectElement>) {
    setFocusDate(clampToRange(safeDate(year, Number(event.target.value), focusDate.getDate())));
  }

  function handleYearSelect(event: React.ChangeEvent<HTMLSelectElement>) {
    setFocusDate(clampToRange(safeDate(Number(event.target.value), month, focusDate.getDate())));
  }

  function handleGridKeyDown(event: ReactKeyboardEvent<HTMLDivElement>, cell: DayCell) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        setFocusDate(addDays(focusDate, -1));
        return;
      case 'ArrowRight':
        event.preventDefault();
        setFocusDate(addDays(focusDate, 1));
        return;
      case 'ArrowUp':
        event.preventDefault();
        setFocusDate(addDays(focusDate, -7));
        return;
      case 'ArrowDown':
        event.preventDefault();
        setFocusDate(addDays(focusDate, 7));
        return;
      case 'Home': {
        event.preventDefault();
        const mondayIndex = (focusDate.getDay() + 6) % 7;
        setFocusDate(addDays(focusDate, -mondayIndex));
        return;
      }
      case 'End': {
        event.preventDefault();
        const mondayIndex = (focusDate.getDay() + 6) % 7;
        setFocusDate(addDays(focusDate, 6 - mondayIndex));
        return;
      }
      case 'PageUp':
        event.preventDefault();
        setFocusDate(clampToRange(safeDate(year, month - 1, focusDate.getDate())));
        return;
      case 'PageDown':
        event.preventDefault();
        setFocusDate(clampToRange(safeDate(year, month + 1, focusDate.getDate())));
        return;
      case 'Enter':
      case ' ':
        event.preventDefault();
        selectDay(cell);
        return;
      case 'Escape':
        event.preventDefault();
        closePopover(true);
        return;
      default:
    }
  }

  return (
    <div ref={wrapperRef} style={fieldWrap} onBlur={handleWrapperBlur}>
      <div style={inputRowStyle}>
        <input
          ref={inputRef}
          id={id}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholder}
          aria-describedby={formatHintId}
          value={shown}
          disabled={disabled}
          onChange={handleTextChange}
          onMouseDown={handleInputMouseDown}
          onKeyDown={handleInputKeyDown}
          style={{ ...inputStyle, ...(compact ? compactInputStyle : null), opacity: disabled ? 0.6 : 1 }}
        />
        <span id={formatHintId} style={srOnlyStyle}>
          Date format: day, month, year — for example 27/10/1996.
        </span>
        <button
          ref={iconRef}
          type="button"
          aria-label="Choose date"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? popoverId : undefined}
          disabled={disabled}
          onClick={() => (open ? closePopover(false) : openPopover('button', true))}
          style={{ ...iconButtonStyle, ...(compact ? compactIconButtonStyle : null) }}
        >
          <CalendarGlyph />
        </button>
      </div>

      {open ? (
        <div id={popoverId} role="dialog" aria-label="Choose date" style={popoverStyle}>
          <div style={{ display: 'flex', gap: 'var(--gps-s-2)', marginBottom: 'var(--gps-s-2)' }}>
            <select aria-label="Month" value={month} onChange={handleMonthSelect} style={selectStyle}>
              {MONTH_NAMES.map((name, idx) => (
                <option
                  key={name}
                  value={idx}
                  disabled={
                    (year === maxYear && effectiveMax ? idx > (parseIso(effectiveMax)?.getMonth() ?? 11) : false) ||
                    (year === minYear && effectiveMin ? idx < (parseIso(effectiveMin)?.getMonth() ?? 0) : false)
                  }
                >
                  {name}
                </option>
              ))}
            </select>
            <select aria-label="Year" value={year} onChange={handleYearSelect} style={selectStyle}>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* onFocus (it bubbles) rather than a click handler: however the grid
              is reached — Tab, ArrowDown, a mouse — that is the moment the grid
              owns the keyboard and arrow navigation may move focus with it. */}
          <div role="grid" aria-label={`${MONTH_NAMES[month]} ${year}`} onFocus={() => setGridFocus(true)}>
            <div role="row" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {WEEKDAYS.map((wd) => (
                <span
                  key={wd.abbr}
                  role="columnheader"
                  aria-label={wd.full}
                  style={{ textAlign: 'center', fontSize: '11px', fontWeight: 700, color: 'var(--gps-muted)', padding: '4px 0' }}
                >
                  {wd.abbr}
                </span>
              ))}
            </div>
            {weeks.map((week) => (
              <div key={week[0].iso} role="row" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
                {week.map((cell) => {
                  const isOutOfMonth = !cell.inMonth;
                  const isSelectable = cell.inMonth && !cell.disabled;
                  return (
                    <div
                      key={cell.iso}
                      ref={(el) => {
                        // Only in-month cells participate in the roving tabindex.
                        if (cell.inMonth) dayRefs.current[cell.iso] = el;
                      }}
                      role="gridcell"
                      aria-selected={cell.inMonth && cell.iso === value}
                      aria-current={cell.inMonth && cell.iso === today ? 'date' : undefined}
                      aria-disabled={isOutOfMonth || cell.disabled || undefined}
                      tabIndex={cell.inMonth && cell.iso === focusIso ? 0 : -1}
                      onClick={isSelectable ? () => selectDay(cell) : undefined}
                      onKeyDown={cell.inMonth ? (e) => handleGridKeyDown(e, cell) : undefined}
                      style={{
                        minWidth: '44px',
                        minHeight: '44px',
                        display: 'grid',
                        placeItems: 'center',
                        margin: '1px auto',
                        borderRadius: 'var(--gps-r-pill)',
                        fontSize: '14px',
                        cursor: isSelectable ? 'pointer' : 'not-allowed',
                        opacity: isOutOfMonth ? 0.25 : cell.disabled ? 0.4 : 1,
                        background: cell.inMonth && cell.iso === value ? 'var(--gps-primary)' : 'transparent',
                        color: cell.inMonth && cell.iso === value ? 'var(--gps-on-accent)' : 'var(--gps-text)',
                        fontWeight: cell.inMonth && (cell.iso === value || cell.iso === today) ? 700 : 400,
                        border:
                          cell.inMonth && cell.iso === today && cell.iso !== value
                            ? '1.5px solid var(--gps-primary)'
                            : '1.5px solid transparent',
                        outlineOffset: '2px',
                      }}
                    >
                      {cell.date.getDate()}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { todayIso };
